import styles from './OwnershipTimeline.module.css'
import Avatar from './Avatar'
import Icon from './Icon'

// "2026-06-03T09:15:00+00:00" → "Jun 3 · 09:15". Falls back to the raw value.
function formatAt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${date} · ${time}`
}

/**
 * OwnershipTimeline — the case detail right-rail chain of custody. Renders the
 * ownership_timeline events from GET /api/cases/{id}: a Created bookend carrying the
 * pre-assignment intake window (created → first owner), one row per owner (person or
 * team) carrying its tenure duration + share of the lifecycle, and a Resolved bookend
 * (no metrics). Any row with an unknown span (e.g. missing chain timestamps) simply
 * omits the duration/percent — never a crash.
 *
 * Props:
 *   events {Array<{event, kind, at, duration, pct_of_lifecycle}>} — duration and
 *     pct_of_lifecycle are preformatted display strings from the backend (e.g. "4.3 hrs",
 *     "42%", "<1%", "≥99%") or null; render them verbatim, no unit appended here.
 */
export default function OwnershipTimeline({ events = [] }) {
  return (
    <section className={styles.panel}>
      <h2 className={styles.heading}>Ownership Timeline</h2>
      <ol className={styles.timeline}>
        {events.map((e, i) => (
          <li key={i} className={styles.item}>
            <div className={styles.marker}>
              <Node kind={e.kind} name={e.event} />
              {i < events.length - 1 && <span className={styles.connector} />}
            </div>
            <div className={styles.body}>
              <span className={`${styles.name} ${e.kind === 'resolved' ? styles.resolvedName : ''}`}>
                {e.event}
              </span>
              {e.at && <span className={styles.at}>{formatAt(e.at)}</span>}
              {(e.duration || e.pct_of_lifecycle != null) && (
                <div className={styles.metrics}>
                  {e.duration && <span className={styles.durationPill}>{e.duration}</span>}
                  {e.pct_of_lifecycle != null && (
                    <span className={styles.pct}>{e.pct_of_lifecycle}</span>
                  )}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

const NODE_ICONS = ['created', 'resolved', 'team']

function Node({ kind, name }) {
  if (kind === 'person') {
    return <Avatar name={name} size={30} />
  }
  const className = `${styles.node} ${styles[`node_${kind}`] || styles.node_team}`
  return <span className={className}><Icon name={NODE_ICONS.includes(kind) ? kind : 'team'} /></span>
}
